import axios from 'axios';
import { getApiUrl } from '@/config/environment';
import {
  getAuthToken,
  translateMessage,
  createSocketManager,
  processNDJSONStream,
  calculateBackoff,
  validateCSVSecurity,
  validateCSVFormat,
  CHUNK_SIZES,
  type SocketManager
} from '@/core/services/upload/utils';

// Standardized type for upload progress tracking
// Dva odvojena progress bara:
// - uploadProgress: Frontend kontrolira (chunk upload) 0-100%
// - processingProgress: Backend kontrolira (CSV obrada) 0-100%
export interface UploadProgress {
  fileName: string;
  uploadProgress: number;      // 0-100% - Frontend chunk upload
  processingProgress: number;  // 0-100% - Backend processing
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  message?: string;
  step?: string;               // Backend step: chunk_assembly, parsing, preprocessing, processing, streaming, complete
  eta?: number | string;       // Raw ETA value from backend
  etaFormatted?: string;       // Formatted ETA string from backend (e.g., "2m 30s")
  // File tracking from backend
  currentFile?: number;        // Current file being processed (1-based)
  totalFiles?: number;         // Total number of files
  // Processing step details from backend
  currentStep?: number;        // Current processing step
  totalSteps?: number;         // Total processing steps
}

export interface ServerResponse {
  success: boolean;
  message: string;
  data?: string[][] | Record<string, unknown>[] | Record<string, unknown>;
  preview?: string[][];
  fileId?: string;
  uploadId?: string;
  totalRows?: number;
  headers?: string[];
  previewRowCount?: number;
  error?: string;
}

export interface AdditionalUploadData {
  selected_columns?: unknown;
  timezone?: string;
  dropdown_count?: number;
  valueColumnName?: string;
}

export class RowDataChunkedUploader {
  // Upload configuration constants
  private static readonly CHUNK_SIZE = CHUNK_SIZES.MEDIUM; // 5MB chunks
  private static readonly API_PATH = '/api/loadRowData';
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_BASE_DELAY = 1000;
  private static readonly REQUEST_TIMEOUT = 30000; // 30 seconds

  // Header option constants (German locale)
  private static readonly HEADER_YES = 'ja';
  private static readonly HEADER_NO = 'nein';

  // Progress tracking constants
  private static readonly MAX_UPLOAD_PROGRESS = 95; // Cap at 95% until finalized
  private static readonly RANDOM_STRING_LENGTH = 10;

  // Socket manager instance
  private static socketManager: SocketManager | null = null;
  private static highestProgress: number = 0;
  private static currentUploadId: string | null = null;

  private static get API_BASE_URL(): string {
    return getApiUrl(this.API_PATH);
  }

  /**
   * Get or create socket manager instance
   */
  private static getSocketManager(): SocketManager {
    if (!this.socketManager) {
      this.socketManager = createSocketManager({
        transports: ['polling', 'websocket'],
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });
    }
    return this.socketManager;
  }

  private static joinUploadRoom(uploadId: string): void {
    const socketManager = this.getSocketManager();

    // Leave previous room if exists
    if (this.currentUploadId && this.currentUploadId !== uploadId) {
      socketManager.leaveRoom(this.currentUploadId);
    }

    // Remove all previous upload_progress listeners
    socketManager.off('upload_progress');

    this.currentUploadId = uploadId;
    socketManager.joinRoom(uploadId);
  }

  /**
   * Initialize a new upload session
   * Generates a unique upload ID for tracking the session
   * @returns Unique uploadId for tracking the session
   */
  public static async initializeUpload(): Promise<string> {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, this.RANDOM_STRING_LENGTH);
    const uploadId = `${timestamp}-${randomStr}`;
    return uploadId;
  }

  /**
   * Upload a file in chunks with progress tracking
   */
  public static async uploadFile(
    file: File,
    uploadId: string,
    delimiter: string = ',',
    hasHeader: boolean = true,
    onProgress?: (progress: UploadProgress) => void,
    additionalData?: Record<string, unknown>,
    onUploadComplete?: () => Promise<void>
  ): Promise<ServerResponse> {
    this.highestProgress = 0; // Reset progress tracking for new upload

    // CSV Security and Format validation for CSV files
    if (file.name.toLowerCase().endsWith('.csv')) {
      const fileContent = await file.text();

      // Security validation - check for injection attacks
      const securityResult = validateCSVSecurity(fileContent);
      if (!securityResult.isValid) {
        throw new Error(`Security validation failed: ${securityResult.error}`);
      }

      // Format validation - check column consistency
      const formatResult = validateCSVFormat(fileContent, { minColumns: 2, minLines: 2 });
      if (!formatResult.isValid) {
        throw new Error(`CSV format validation failed: ${formatResult.error}`);
      }
    }

    this.joinUploadRoom(uploadId);
    const socketManager = this.getSocketManager();

    // Track if we're still in upload phase (frontend handles progress)
    // vs processing phase (backend handles progress via WebSocket)
    let isUploadPhaseComplete = false;

    const progressHandler = (progressData: Record<string, unknown>) => {
      if (progressData.uploadId !== uploadId || !onProgress) {
        return;
      }

      const backendStatus = progressData.status as string;

      // IMPORTANT: Ignore backend 'uploading' status messages
      // Frontend handles upload progress to avoid oscillation
      if (backendStatus === 'uploading' && !isUploadPhaseComplete) {
        return;
      }

      // Mark upload phase as complete when we receive processing status
      if (backendStatus === 'processing' || backendStatus === 'completed' || backendStatus === 'error') {
        isUploadPhaseComplete = true;
      }

      // Backend sends processing progress - upload is already complete at this point
      const backendProgress = Math.min(Math.max(Number(progressData.progress) || 0, 0), 100);

      // Prevent progress regression - only allow progress to go up
      if (backendStatus !== 'error' && backendProgress < this.highestProgress) {
        return;
      }

      if (backendProgress > this.highestProgress) {
        this.highestProgress = backendProgress;
      }

      // Translate message using utility function
      const translatedMessage = progressData.messageKey
        ? translateMessage(
            progressData.messageKey as string,
            progressData.messageParams as Record<string, unknown> | undefined,
            progressData.message as string | undefined
          )
        : (progressData.message as string | undefined);

      const mappedProgress: UploadProgress = {
        fileName: file.name,
        uploadProgress: 100,
        processingProgress: backendProgress,
        status: backendStatus as 'uploading' | 'processing' | 'completed' | 'error',
        error: backendStatus === 'error' ? String(progressData.message) : undefined,
        message: translatedMessage,
        step: progressData.step as string | undefined,
        eta: progressData.eta as number | string | undefined,
        etaFormatted: progressData.etaFormatted as string | undefined,
        currentStep: progressData.currentStep !== undefined ? Number(progressData.currentStep) : undefined,
        totalSteps: progressData.totalSteps !== undefined ? Number(progressData.totalSteps) : undefined,
        currentFile: progressData.currentFile as number | undefined,
        totalFiles: progressData.totalFiles as number | undefined
      };

      onProgress(mappedProgress);
    };

    socketManager.on('upload_progress', progressHandler);

    const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE);
    onProgress?.({
      fileName: file.name,
      uploadProgress: 0,
      processingProgress: 0,
      status: 'uploading'
    });

    try {
      // Upload chunks sequentially for Cloud Run Session Affinity compatibility
      await this.uploadChunksSequential(
        file,
        uploadId,
        totalChunks,
        delimiter,
        hasHeader,
        additionalData,
        onProgress
      );

      const finalResponse = await this.finalizeUpload(uploadId);
      onProgress?.({
        fileName: file.name,
        uploadProgress: 100,
        processingProgress: 100,
        status: 'completed'
      });

      // Refresh usage data after successful upload
      if (onUploadComplete) {
        try {
          await onUploadComplete();
        } catch {
          // Don't throw - upload succeeded even if refresh failed
        }
      }

      return finalResponse;
    } catch (error) {
      onProgress?.({
        fileName: file.name,
        uploadProgress: 0,
        processingProgress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      throw error;
    } finally {
      socketManager.off('upload_progress', progressHandler);
    }
  }

  /**
   * Upload chunks sequentially for Cloud Run compatibility
   * Sequential uploads ensure Session Affinity routes all chunks to same instance
   */
  private static async uploadChunksSequential(
    file: File,
    uploadId: string,
    totalChunks: number,
    delimiter: string,
    hasHeader: boolean,
    additionalData: Record<string, unknown> | undefined,
    onProgress: ((progress: UploadProgress) => void) | undefined
  ): Promise<void> {
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * this.CHUNK_SIZE;
      const end = Math.min(start + this.CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      await this.uploadChunk(
        uploadId,
        chunk,
        chunkIndex,
        totalChunks,
        delimiter,
        hasHeader,
        additionalData
      );

      // Update upload progress (0-95%)
      const currentUploadProgress = Math.round(((chunkIndex + 1) / totalChunks) * this.MAX_UPLOAD_PROGRESS);
      onProgress?.({
        fileName: file.name,
        uploadProgress: currentUploadProgress,
        processingProgress: 0,
        status: 'uploading',
        message: `Uploading... ${chunkIndex + 1}/${totalChunks} chunks`
      });
    }
  }

  /**
   * Upload a single chunk to the server with retry mechanism
   */
  private static async uploadChunk(
    uploadId: string,
    chunk: Blob,
    chunkIndex: number,
    totalChunks: number,
    delimiter: string = ',',
    hasHeader: boolean = true,
    additionalData?: Record<string, unknown>,
    retryCount: number = this.MAX_RETRIES
  ): Promise<ServerResponse> {
    try {
      const formData = new FormData();

      formData.append('fileChunk', chunk, 'chunk.csv');
      formData.append('uploadId', uploadId);
      formData.append('chunkIndex', chunkIndex.toString());
      formData.append('totalChunks', totalChunks.toString());
      formData.append('delimiter', delimiter);
      formData.append('hasHeader', hasHeader ? this.HEADER_YES : this.HEADER_NO);

      // Append additional data if provided
      if (additionalData) {
        const { selected_columns, timezone, dropdown_count, valueColumnName } = additionalData;

        if (selected_columns) {
          formData.append('selected_columns', JSON.stringify(selected_columns));
        }
        if (timezone && typeof timezone === 'string') {
          formData.append('timezone', timezone);
        }
        if (dropdown_count) {
          formData.append('dropdown_count', dropdown_count.toString());
        }
        if (valueColumnName && typeof valueColumnName === 'string') {
          formData.append('valueColumnName', valueColumnName);
        }
      }

      const accessToken = await getAuthToken();

      const response = await axios.post(`${this.API_BASE_URL}/upload-chunk`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${accessToken}`
        },
        timeout: this.REQUEST_TIMEOUT
      });

      return response.data;
    } catch (error) {
      // Do NOT retry 4xx client errors (e.g. 413 payload too large, 429
      // rate limited). The backend's answer won't change on replay, so
      // retrying only burns ~30s of backoff while hiding the real error.
      // Surface the backend's JSON error body (error/message + status)
      // immediately instead of the generic axios statusText.
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;
        if (status >= 400 && status < 500) {
          const body = error.response.data as
            | { error?: string; message?: string; error_code?: string }
            | undefined;
          const backendMessage = body?.error || body?.message;
          const err = new Error(
            backendMessage ||
              `Chunk upload failed: ${status} ${error.response.statusText || ''}`.trim()
          ) as Error & { status?: number; code?: string };
          err.status = status;
          if (body?.error_code) err.code = body.error_code;
          throw err;
        }
      }

      if (retryCount > 0) {
        const delay = calculateBackoff(this.MAX_RETRIES - retryCount + 1, this.RETRY_BASE_DELAY);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.uploadChunk(
          uploadId,
          chunk,
          chunkIndex,
          totalChunks,
          delimiter,
          hasHeader,
          additionalData,
          retryCount - 1
        );
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to upload chunk ${chunkIndex} after multiple attempts: ${errorMessage}`);
    }
  }

  /**
   * Finalize upload and trigger processing
   */
  private static async finalizeUpload(uploadId: string): Promise<ServerResponse> {
    const accessToken = await getAuthToken();

    const response = await fetch(`${this.API_BASE_URL}/finalize-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ uploadId })
    });

    if (!response.ok) {
      // Read error body to get detailed error message + structured code from backend
      let errorMessage = `Server error: ${response.status} ${response.statusText}`;
      let errorCode: string | undefined;
      try {
        const errorBody = await response.json();
        if (typeof errorBody?.error === 'string') {
          errorMessage = errorBody.error;
        } else if (typeof errorBody?.message === 'string') {
          errorMessage = errorBody.message;
        }
        if (typeof errorBody?.error_code === 'string') {
          errorCode = errorBody.error_code;
        }
      } catch {
        // JSON parsing failed, use default error message
      }
      const err = new Error(errorMessage) as Error & {
        code?: string;
        status?: number;
      };
      err.status = response.status;
      if (errorCode) err.code = errorCode;
      throw err;
    }

    // Check if response is streaming NDJSON
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/x-ndjson') || contentType?.includes('text/event-stream')) {
      const data = await processNDJSONStream<Record<string, unknown>>(response);
      return this.formatStreamingData(data);
    }

    // Regular JSON response
    return await response.json();
  }

  /**
   * Format streaming data to expected ServerResponse format
   */
  private static formatStreamingData(data: Record<string, unknown>[]): ServerResponse {
    const formattedData: string[][] = [];

    if (data.length > 0) {
      // Get column names from first row
      const columns = Object.keys(data[0]);

      // Add header row
      formattedData.push(columns);

      // Add data rows
      for (const row of data) {
        const rowArray = columns.map(col => {
          const val = row[col];
          if (val === null || val === undefined) return '';
          return String(val);
        });
        formattedData.push(rowArray);
      }
    }

    return {
      success: true,
      message: 'Upload completed',
      data: formattedData
    };
  }

  /**
   * Check upload status
   */
  public static async checkUploadStatus(uploadId: string): Promise<ServerResponse> {
    const accessToken = await getAuthToken();

    const response = await axios.get(`${this.API_BASE_URL}/upload-status/${uploadId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data;
  }

  /**
   * Cancel upload and clean up server resources
   */
  public static async cancelUpload(uploadId: string): Promise<ServerResponse> {
    const socketManager = this.getSocketManager();
    socketManager.leaveRoom(uploadId);

    const accessToken = await getAuthToken();

    const response = await axios.post(`${this.API_BASE_URL}/cancel-upload`, {
      uploadId
    }, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    return response.data;
  }
}

export default RowDataChunkedUploader;
